import React, { useState } from 'react';
import * as Yup from 'yup';

import { makeValidate } from 'mui-rff';

import { FormControl, Icon, DatePicker } from 'components/ui';
import FormDialog from 'components/Dialogs/FormDialog';
import FormAutocomplete from '../FormAutocomplete';
import { useGetList } from '../../../pages/CarePlans/_service/hooks/useGetList';
import { useTranslation } from 'react-i18next';
import formatISO from 'date-fns/formatISO';
import { useDebounce } from 'use-debounce';

import { useRun_Care_PlanMutation } from 'generated/graphql';
import useAddCarePlanToPatientGroup from 'pagesAdmin/SettingsPage/TabPatientGroups/hooks/useAddCarePlanToPatientGroup';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import useDialog from 'lib/hooks/useDialog';
import ConfirmationDialog from 'components/Dialogs/ConfirmationDialog';

const schema = Yup.object().shape({
  planDefinition: Yup.object().shape({
    id: Yup.string().required('Required'),
  }),
  startDate: Yup.date().typeError('Invalid date').required('Required'),
});

//@ts-ignore
const validate = makeValidate(schema);

function AddCarePlanDialog(props: Props) {
  const { close, mode, patient, patientGroup, isOpen } = props;
  const confirmationDialog = useDialog();
  
  // Get careTeamIds only for individual patient mode
  //@ts-ignore
  const patientCareTeamIds = mode === 'patient' ? patient?.careTeams?.map((i: any) => i.id) : undefined;

  const [search, setSearch] = useState('');
  const [searchWithDelay] = useDebounce(search, 300);

  const { data, loading: loadingList, loadMore } = useGetList({
    perPage: 60,
    filter: {
      title: searchWithDelay, 
      status: 'active', 
      // Patient modes: filter by patientId
      ...(mode === 'patient' && { patientId: patient?.id }),
      // Patient group mode: filter by careTeamId
      ...(mode === 'patientGroup' && { careTeamId: patientGroup?.careTeam?.id }),
    },
  });

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [runCarePlan] = useRun_Care_PlanMutation();
  const { onAddCarePlan, loading } = useAddCarePlanToPatientGroup(close);

  const handleGroupCarePlan = async (values: any) => {
    const { isConfirmed } = await confirmationDialog.open({
      data: {
        description: t(
          "You're about to assign a care plan to {{number}} patients in this group. Are you sure you want to proceed?",
          { number: patientGroup?.patientsTotalCount }
        ),
        confirmText: t('Confirm'),
        cancelText: t('Cancel'),
      },
    });

    if (!isConfirmed) return;

    await onAddCarePlan({
      variables: {
        record: {
          planDefinitionId: values.planDefinitionId,
          startDate: values.startDate,
          patientGroupId: patientGroup?.id,
        },
      },
    });
  };

  const handleSingleCarePlan = async (values: any) => {
    await runCarePlan({
      variables: {
        //@ts-ignore
        record: {
          planDefinitionId: values.planDefinitionId,
          startDate: values.startDate,
          patientId: patient?.id,
          careTeamId: values.careTeamId,
        },
      },
      onCompleted: (res) => {
        //@ts-ignore
        const result = res?.requestGroup?.create;
        if (result.ok) {
          enqueueSnackbar(t('Care plan added successfully'), {
            variant: 'success',
          });
          close(result);
        } else {
          enqueueSnackbar(result?.error?.message, {
            variant: 'error',
          });
        }
      },
    });
  };

  const onSave = async (values: any) => {
    const data = {
      planDefinitionId: values.planDefinition.id,
      startDate: formatISO(values?.startDate),
      // Only add careTeamId for segregated care pathways
      ...(values?.planDefinition?.type === 'SEGREGATED_CARE_TEAMS' && {
        careTeamId: patientCareTeamIds?.[0]
      })
    };

    if (!data.planDefinitionId) {
      return;
    }

    try {
      if (mode === 'patientGroup') {
        await handleGroupCarePlan(data);
      } else {
        await handleSingleCarePlan(data);
      }
    } catch (error) {
      console.error('Error saving care plan:', error);
      enqueueSnackbar(t('Failed to save care plan'), {
        variant: 'error',
      });
    }
  };

  const actions = [
    <LoadingButton
      loading={loading}
      key="save"
      type="submit"
      color="primary"
      variant="contained"
    >
      <Icon icon="file" size="1x" mr={10} /> {t('Save')}
    </LoadingButton>,
  ];

  return (
    //@ts-ignore
    <FormDialog
      title={t('Add protocol')}
      onSubmit={onSave}
      validate={validate}
      actions={actions}
      onClose={close}
      isOpen={isOpen}
    >
      {(values: any) => {
        return (
          <div>
            <FormControl
              sx={{ input: { height: '40px !importnant' } }}
              label={t('Care plan')}
            >
              <FormAutocomplete
                name="planDefinition"
                placeholder={t('Choose protocol')}
                getOptionLabel={(i: any) => i?.title}
                isOptionEqualToValue={(option: any, value: any) =>
                  option?.id === value?.id
                }
                getOptionValue={(i: any) => i}
                loading={loadingList || loading}
                inputOnChange={setSearch}
                clearInputAfterSelect={false}
                onScroll={loadMore}
                options={
                  data?.planDefinition?.planDefinitionPagination?.items || []
                }
              />
            </FormControl>

            <FormControl label={t('Publication date')}>
              <DatePicker
                name="startDate"
                id="version-publish-date"
                placeholder={`${t('Choose date')}...`}
                openTo="month"
                disablePast={true}
              />
            </FormControl>

            {confirmationDialog.isOpen && (
              <ConfirmationDialog {...confirmationDialog} />
            )}
          </div>
        );
      }}
    </FormDialog>
  );
}

export default AddCarePlanDialog;

interface Props {
  close: (values: any) => void;
  isOpen: boolean;
  mode: 'patient' | 'patientGroup';
  patient?: {
    id: string;
    name?: any;
    careTeams?: Array<{ id: string; name?: string }>;
  };
  patientGroup?: {
    id: string;
    title?: string;
    patientsTotalCount?: number;
    careTeam?: { id: string; name?: string };
  };
}
